import React from "react";
import { Input } from "@/components/ui/input";
import { PhoneCodePicker } from "@/components/ui/phone-code-picker";
import { SearchablePicklist } from "@/components/ui/searchable-picklist";
import Toast from "@/pages/portal/components/Toast";

// ─── Field Configuration ──────────────────────────────────────────────────────
// All fields visible by default. Use Django admin `hidden_fields` to hide specific fields.
// Set isRequired: false to make a field optional (no validation).
// ─────────────────────────────────────────────────────────────────────────────
export const PERSONAL_INFO_FIELD_CONFIG: Record<string, { isVisible: boolean; isRequired: boolean; fieldType?: string }> = {
    firstName:       { isVisible: true,  isRequired: true  },
    preferredName:   { isVisible: true,  isRequired: false },
    lastName:        { isVisible: true,  isRequired: true  },
    citizenship:     { isVisible: true,  isRequired: true, fieldType: "country" },
    secondaryCitizenship: { isVisible: true, isRequired: false, fieldType: "country" },
    gender:          { isVisible: true,  isRequired: true  },
    nationalIdentityNumber:  { isVisible: true,  isRequired: true  },
    dateOfBirth:     { isVisible: true,  isRequired: true  },
    countryOfBirth:  { isVisible: true,  isRequired: true, fieldType: "country" },
    cityOfBirth:     { isVisible: true,  isRequired: false },
    // Identity Document fields (passport)
    identityNumber:  { isVisible: true,  isRequired: true  },
    identityDateOfIssue:   { isVisible: true,  isRequired: true  },
    identityDateOfExpiry:  { isVisible: true,  isRequired: true  },
    identityIssuingCountry: { isVisible: true, isRequired: true, fieldType: "country" },
    email:           { isVisible: true,  isRequired: true  },
    mobileNumber:    { isVisible: true,  isRequired: true  },
    fatherName:      { isVisible: true,  isRequired: false },
    motherName:      { isVisible: true,  isRequired: false },
    addressCountry:  { isVisible: true,  isRequired: true, fieldType: "country" },
    addressStreet:   { isVisible: true,  isRequired: true  },
    addressCity:     { isVisible: true,  isRequired: true  },
    addressZip:      { isVisible: true,  isRequired: true  },
    isAlumni:        { isVisible: true,  isRequired: false },
};


// Characters to STRIP per field (applied on every keystroke)
// Uses Unicode property escapes (\p{L}) with the `u` flag so ALL Unicode letters
// are allowed, including Turkish (İ ı Ğ ğ Ş ş) and other non-Latin-1 scripts.
const FIELD_STRIP: Record<string, RegExp> = {
    // Name fields: any Unicode letter, spaces, hyphens, apostrophes — no digits
    firstName:      /[^\p{L}\s'\-]/gu,
    preferredName:  /[^\p{L}\s'\-]/gu,
    lastName:       /[^\p{L}\s'\-]/gu,
    fatherName:     /[^\p{L}\s'\-]/gu,
    motherName:     /[^\p{L}\s'\-]/gu,
    // Country fields: no strip — selected from dropdown, may contain parens e.g. "Türkiye (Turkey)"
    // City: any Unicode letter + spaces, no digits
    addressCity:    /[^\p{L}\s]/gu,
    cityOfBirth:    /[^\p{L}\s]/gu,
    // Phone: digits only — no spaces, +, -, (, ) etc.
    mobileNumber:   /[^0-9]/g,
    // Street: any Unicode letter, digits, spaces, comma, period, hyphen
    addressStreet:  /[^\p{L}\d\s,.\-]/gu,
    // Zip: letters, digits, spaces, hyphen (covers UK, US, EU formats)
    addressZip:     /[^a-zA-Z0-9\s\-]/g,
    // Note: identityNumber has dedicated handling in handleChange (uppercase + strip + max 20 chars)
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// TC Kimlik Numarası validation (only for Turkey)
// Returns true if valid, false if invalid
const validateTCKimlik = (value: string): boolean => {
    // Must be exactly 11 digits
    if (!/^\d{11}$/.test(value)) return false;
    
    // Cannot start with 0
    if (value.startsWith("0")) return false;
    
    // Cannot be repeated digits (11111111110, 22222222220, etc.)
    const repeatedPatterns = ["11111111110", "22222222220", "33333333330", "44444444440", 
                              "55555555550", "66666666660", "77777777770", "88888888880", "99999999990"];
    if (repeatedPatterns.includes(value)) return false;
    
    const digits = value.split("").map(Number);
    
    // 10th digit check: ((sum of 1,3,5,7,9 digits) × 7 - (sum of 2,4,6,8 digits)) mod 10 = 10th digit
    const oddSum = digits[0] + digits[2] + digits[4] + digits[6] + digits[8];
    const evenSum = digits[1] + digits[3] + digits[5] + digits[7];
    const tenthDigitCheck = ((oddSum * 7) - evenSum) % 10;
    // Handle negative modulo
    const expectedTenth = tenthDigitCheck < 0 ? tenthDigitCheck + 10 : tenthDigitCheck;
    if (digits[9] !== expectedTenth) return false;
    
    // 11th digit check: (sum of first 10 digits) mod 10 = 11th digit
    const first10Sum = digits.slice(0, 10).reduce((a, b) => a + b, 0);
    if (digits[10] !== (first10Sum % 10)) return false;
    
    return true;
};

// Hint shown below the field when an invalid char is blocked
const FIELD_HINT: Record<string, string> = {
    firstName:      "Letters only",
    preferredName:  "Letters only",
    lastName:       "Letters only",
    fatherName:     "Letters only",
    motherName:     "Letters only",
    addressCity:    "Letters only",
    cityOfBirth:    "Letters only",
    nationalIdentityNumber: "Digits only (TC Kimlik)",
    mobileNumber:   "Digits only",
    addressStreet:  "No special characters",
    addressZip:     "Letters, digits and hyphens only",
    identityNumber: "Uppercase letters and digits only (max 20)",
};

interface PersonalInfoValues {
    firstName: string;
    preferredName: string;
    lastName: string;
    citizenship: string;
    secondaryCitizenship: string;
    gender: string;
    nationalIdentityNumber: string;
    dateOfBirth: string;
    countryOfBirth: string;
    cityOfBirth: string;
    // Identity Document (passport)
    identityNumber: string;
    identityDateOfIssue: string;
    identityDateOfExpiry: string;
    identityIssuingCountry: string;
    email: string;
    mobileCode: string;
    mobileNumber: string;
    fatherName: string;
    motherName: string;
    addressCountry: string;
    addressStreet: string;
    addressCity: string;
    addressZip: string;
    isAlumni: boolean;
}

interface PersonalInfoProps {
    values: PersonalInfoValues;
    onChange: (field: string, value: string | boolean) => void;
    onValidSubmit?: () => void;
    fieldConfig?: Record<string, { is_visible: boolean; is_required: boolean; field_type?: string }>;
    countries?: string[];
    hiddenFields?: string[];
    genderIdentities?: { label: string; value: string }[];
    institutionCountry?: string | null;
    error?: string | null;
}

// Map frontend field names to backend field names for hidden_fields matching
const FIELD_NAME_MAP: Record<string, string> = {
    firstName: 'first_name',
    preferredName: 'preferred_name',
    lastName: 'last_name',
    citizenship: 'citizenship',
    secondaryCitizenship: 'secondary_citizenship',
    gender: 'gender',
    nationalIdentityNumber: 'national_identity_number',
    dateOfBirth: 'date_of_birth',
    countryOfBirth: 'country_of_birth',
    cityOfBirth: 'city_of_birth',
    identityNumber: 'identity_number',
    identityDateOfIssue: 'identity_date_of_issue',
    identityDateOfExpiry: 'identity_date_of_expiry',
    identityIssuingCountry: 'identity_issuing_country',
    email: 'email',
    mobileNumber: 'phone',
    fatherName: 'father_name',
    motherName: 'mother_name',
    addressCountry: 'address_country',
    addressStreet: 'address_street',
    addressCity: 'address_city',
    addressZip: 'address_zip_code',
    isAlumni: 'university_alumni',
};

// Minimum age requirement
const MIN_AGE = 13;
const MAX_AGE = 100;
const PASSPORT_ISSUE_MAX_AGE_YEARS = 10;
const PASSPORT_EXPIRY_MAX_YEARS = 10;

const formatDateForInput = (inputDate: Date): string => {
    const year = inputDate.getFullYear();
    const month = String(inputDate.getMonth() + 1).padStart(2, "0");
    const day = String(inputDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

// Get maximum allowed birth date (today minus MIN_AGE years) in YYYY-MM-DD format
const getMaxBirthDate = (): string => {
    const currentDate = new Date();
    const maxDate = new Date(currentDate.getFullYear() - MIN_AGE, currentDate.getMonth(), currentDate.getDate());
    return formatDateForInput(maxDate);
};

// Get minimum allowed birth date (today minus MAX_AGE years) in YYYY-MM-DD format
const getMinBirthDate = (): string => {
    const currentDate = new Date();
    const minDate = new Date(currentDate.getFullYear() - MAX_AGE, currentDate.getMonth(), currentDate.getDate());
    return formatDateForInput(minDate);
};

const getMinPassportIssueDate = (): string => {
    const currentDate = new Date();
    const minDate = new Date(
        currentDate.getFullYear() - PASSPORT_ISSUE_MAX_AGE_YEARS,
        currentDate.getMonth(),
        currentDate.getDate(),
    );
    return formatDateForInput(minDate);
};

const getMaxPassportExpiryDate = (): string => {
    const currentDate = new Date();
    const maxDate = new Date(
        currentDate.getFullYear() + PASSPORT_EXPIRY_MAX_YEARS,
        currentDate.getMonth(),
        currentDate.getDate(),
    );
    return formatDateForInput(maxDate);
};

export default function PersonalInfo({ values, onChange, onValidSubmit, fieldConfig, countries = [], hiddenFields = [], genderIdentities = [], institutionCountry, error }: PersonalInfoProps) {
    const [errors, setErrors] = React.useState<Record<string, boolean>>({});
    const [formatHints, setFormatHints] = React.useState<Record<string, boolean>>({});
    const [showValidationToast, setShowValidationToast] = React.useState(false);
    const hintTimers = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});

    // show / req / showSection — check hiddenFields first, then fieldConfig from branding API, fall back to local PERSONAL_INFO_FIELD_CONFIG
    const show = (field: string) => {
        // Check if field is in hiddenFields (using backend field name)
        const backendFieldName = FIELD_NAME_MAP[field] || field;
        if (hiddenFields.includes(backendFieldName)) return false;

        // Special visibility logic for nationalIdentityNumber:
        // (1 OR 2) AND 3
        // Condition 1: Institution country = Applicant's Citizenship
        // Condition 2: Institution country = Applicant's Secondary Citizenship
        // Condition 3: Field not in hiddenFields (already checked above)
        if (field === "nationalIdentityNumber" && institutionCountry) {
            const citizenshipMatches = values.citizenship === institutionCountry;
            const secondaryCitizenshipMatches = values.secondaryCitizenship === institutionCountry;
            if (!citizenshipMatches && !secondaryCitizenshipMatches) {
                return false;
            }
        }

        // Passport/Identity fields visibility logic:
        // 1. If identity_number (parent) is in hiddenFields → ALL passport fields HIDDEN
        // 2. If applicant is NOT foreign (when institutionCountry is known) → HIDDEN
        const passportFields = ["identityNumber", "identityDateOfIssue", "identityDateOfExpiry", "identityIssuingCountry"];
        if (passportFields.includes(field)) {
            // Parent check: If identity_number is in hiddenFields, hide ALL passport fields (always applies)
            if (hiddenFields.includes("identity_number")) {
                return false;
            }
            // Foreign/local check: Only apply when institutionCountry is available
            if (institutionCountry) {
                const citizenshipIsForeign = values.citizenship && values.citizenship !== institutionCountry;
                const secondaryCitizenshipIsForeign = values.secondaryCitizenship && values.secondaryCitizenship !== institutionCountry;
                // Show only if at least one citizenship is foreign
                if (!citizenshipIsForeign && !secondaryCitizenshipIsForeign) {
                    return false;
                }
            }
        }
        
        if (fieldConfig && field in fieldConfig) return fieldConfig[field].is_visible !== false;
        return PERSONAL_INFO_FIELD_CONFIG[field]?.isVisible !== false;
    };
    const req = (field: string) => {
        if (fieldConfig && field in fieldConfig) return fieldConfig[field].is_required === true;
        return PERSONAL_INFO_FIELD_CONFIG[field]?.isRequired === true;
    };
    const showSection = (...fields: string[]) => fields.some(show);
    const fieldType = (field: string): string | undefined => {
        if (fieldConfig && field in fieldConfig) return fieldConfig[field].field_type;
        return PERSONAL_INFO_FIELD_CONFIG[field]?.fieldType;
    };

    // Derive required fields from fieldConfig (branding API) if provided, otherwise fall back to local config
    // Hidden fields are never required - use show() to respect all visibility rules including country-based logic
    const requiredFields = Object.keys(PERSONAL_INFO_FIELD_CONFIG).filter((field) => 
        show(field) && (
            fieldConfig && field in fieldConfig 
                ? fieldConfig[field].is_required === true
                : PERSONAL_INFO_FIELD_CONFIG[field].isRequired
        )
    ) as (keyof PersonalInfoValues)[];

    const handleChange = (field: string, value: string | boolean) => {
        let filtered = value;
        // For nationalIdentityNumber, apply country-specific rules ONLY for Turkey
        if (field === "nationalIdentityNumber" && typeof filtered === "string") {
            if (institutionCountry === "Türkiye (Turkey)") {
                // Turkey: digits only, max 11 characters
                const beforeFilter = filtered;
                filtered = filtered.replace(/[^0-9]/g, "").slice(0, 11);
                // Show hint if something was stripped
                if (filtered !== beforeFilter) {
                    setFormatHints((prev) => ({ ...prev, [field]: true }));
                    clearTimeout(hintTimers.current[field]);
                    hintTimers.current[field] = setTimeout(() => {
                        setFormatHints((prev) => ({ ...prev, [field]: false }));
                    }, 1500);
                }
            }
            // Other countries: NO filtering, accept any input
            onChange(field, filtered);
            if (errors[field]) setErrors((prev) => ({ ...prev, [field]: false }));
            return;
        }
        // For identityNumber (passport): uppercase A-Z and 0-9 only, max 20 characters
        if (field === "identityNumber" && typeof filtered === "string") {
            const beforeFilter = filtered;
            // Convert to uppercase, then strip non-alphanumeric, then limit to 20 chars
            filtered = filtered.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 20);
            if (filtered !== beforeFilter) {
                setFormatHints((prev) => ({ ...prev, [field]: true }));
                clearTimeout(hintTimers.current[field]);
                hintTimers.current[field] = setTimeout(() => {
                    setFormatHints((prev) => ({ ...prev, [field]: false }));
                }, 1500);
            }
            onChange(field, filtered);
            if (errors[field]) setErrors((prev) => ({ ...prev, [field]: false }));
            return;
        }
        // Uppercase national identity number BEFORE stripping so lowercase letters are preserved
        if (typeof value === "string" && FIELD_STRIP[field]) {
            const beforeStrip = filtered as string;
            filtered = beforeStrip.replace(FIELD_STRIP[field], "");
            // If something was stripped, flash a format hint
            if (filtered !== beforeStrip) {
                setFormatHints((prev) => ({ ...prev, [field]: true }));
                clearTimeout(hintTimers.current[field]);
                hintTimers.current[field] = setTimeout(() => {
                    setFormatHints((prev) => ({ ...prev, [field]: false }));
                }, 1500);
            }
        }
        onChange(field, filtered);
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: false }));
    };

    const clearHint = (field: string) => {
        if (formatHints[field]) {
            clearTimeout(hintTimers.current[field]);
            setFormatHints((prev) => ({ ...prev, [field]: false }));
        }
    };

    const fieldErrorClass = (field: string) => (errors[field] || formatHints[field]) ? "border-red-400 focus-visible:ring-2 focus-visible:ring-red-200" : "border-slate-800";
    // Helper text shown under a field
    const hint = (field: string) => {
        if (errors[field]) {
            // Special error message for TC Kimlik validation
            if (field === "nationalIdentityNumber" && institutionCountry === "Türkiye (Turkey)" && values.nationalIdentityNumber) {
                return <p className="text-[10px] text-red-500 font-bold mt-1 ml-1">Invalid TC Kimlik number</p>;
            }
            // Special error messages for identity document fields
            if (field === "identityDateOfIssue" && values.identityDateOfIssue) {
                return <p className="text-[10px] text-red-500 font-bold mt-1 ml-1">Must be within the last 10 years</p>;
            }
            if (field === "identityDateOfExpiry" && values.identityDateOfExpiry) {
                return <p className="text-[10px] text-red-500 font-bold mt-1 ml-1">Must be between tomorrow and 10 years from today</p>;
            }
            if (field === "dateOfBirth" && values.dateOfBirth) {
                return <p className="text-[10px] text-red-500 font-bold mt-1 ml-1">Age must be between 13 and 100 years</p>;
            }
            return <p className="text-[10px] text-red-500 font-bold mt-1 ml-1">This field is required</p>;
        }
        if (formatHints[field] && FIELD_HINT[field]) return <p className="text-[10px] text-red-500 font-bold mt-1 ml-1">{FIELD_HINT[field]}</p>;
        return null;
    };
    // Label suffix — shows * only when field is required per config
    const star = (field: string) => req(field) ? <span className="text-red-500"> *</span> : null;

    return (
        <div className="relative">
        {showValidationToast && (
            <Toast
                message="Please fix the highlighted fields before continuing."
                type="error"
                position="top"
                onClose={() => setShowValidationToast(false)}
                duration={5000}
            />
        )}
        <form
            id="personal-info-form"
            onSubmit={(ev) => {
                ev.preventDefault();
                const newErrors: Record<string, boolean> = {};
                requiredFields.forEach((fieldName) => {
                    if (!String(values[fieldName] ?? "").trim()) newErrors[fieldName] = true;
                });
                // Email format check
                if (show("email") && values.email && !EMAIL_REGEX.test(values.email)) {
                    newErrors["email"] = true;
                }
                // TC Kimlik validation (only for Turkey)
                if (show("nationalIdentityNumber") && institutionCountry === "Türkiye (Turkey)" && values.nationalIdentityNumber) {
                    if (!validateTCKimlik(values.nationalIdentityNumber)) {
                        newErrors["nationalIdentityNumber"] = true;
                    }
                }
                // Identity document validations
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                // Helper to parse YYYY-MM-DD as local date (not UTC)
                const parseLocalDate = (dateStr: string) => {
                    const [year, month, day] = dateStr.split('-').map(Number);
                    return new Date(year, month - 1, day);
                };
                // Date of Issue: must be <= today AND >= today - 10 years
                if (show("identityDateOfIssue") && values.identityDateOfIssue) {
                    const issueDate = parseLocalDate(values.identityDateOfIssue);
                    const tenYearsAgo = new Date(today);
                    tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
                    if (issueDate > today || issueDate < tenYearsAgo) {
                        newErrors["identityDateOfIssue"] = true;
                    }
                }
                // Date of Expiry: must be > today (future date)
                if (show("identityDateOfExpiry") && values.identityDateOfExpiry) {
                    const expiryDate = parseLocalDate(values.identityDateOfExpiry);
                    const maxExpiryDate = parseLocalDate(getMaxPassportExpiryDate());
                    if (expiryDate <= today || expiryDate > maxExpiryDate) {
                        newErrors["identityDateOfExpiry"] = true;
                    }
                }
                // Date of Birth: must be between today-100 years and today-13 years
                if (show("dateOfBirth") && values.dateOfBirth) {
                    const birthDate = parseLocalDate(values.dateOfBirth);
                    const minimumBirthDate = parseLocalDate(getMinBirthDate());
                    const maximumBirthDate = parseLocalDate(getMaxBirthDate());
                    if (birthDate < minimumBirthDate || birthDate > maximumBirthDate) {
                        newErrors["dateOfBirth"] = true;
                    }
                }
                if (Object.keys(newErrors).length > 0) {
                    setErrors(newErrors);
                    setShowValidationToast(true);
                    // Scroll to the first field with an error
                    const fieldOrder = Object.keys(PERSONAL_INFO_FIELD_CONFIG);
                    const firstErrorField = fieldOrder.find((f) => newErrors[f]);
                    if (firstErrorField) {
                        const el = document.getElementById(`field-${firstErrorField}`) ?? (firstErrorField === "isAlumni" ? document.getElementById("alumni") : null);
                        el?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                    return;
                }
                onValidSubmit?.();
            }}
            className="space-y-8 md:space-y-12"
        >
            <div>
                <h2 className="text-2xl md:text-4xl font-serif font-black text-[#001B44] leading-tight text-left">
                    Personal Information
                </h2>
                <p className="text-slate-900 mt-2 md:mt-4 text-base md:text-lg font-medium max-w-2xl text-left">
                    Please provide your personal and contact details accurately as they appear on
                    your legal documents.
                </p>
            </div>

            <div className="space-y-8 md:space-y-12">
                {/* Personal Details Section */}
                {showSection("firstName", "preferredName", "lastName", "citizenship", "secondaryCitizenship", "gender", "nationalIdentityNumber", "dateOfBirth", "countryOfBirth", "cityOfBirth") && (
                <section className="space-y-6">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#001B44]" />
                        Personal Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        {show("firstName") && (
                        <div id="field-firstName" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                First Name {star("firstName")}
                            </label>
                            <Input
                                placeholder="Enter your first name"
                                className={`h-12 rounded-xl ${fieldErrorClass("firstName")}`}
                                value={values.firstName}
                                onFocus={() => clearHint("firstName")}
                                onChange={(ev) => handleChange("firstName", ev.target.value)}
                            />
                            {hint("firstName")}
                        </div>
                        )}
                        {show("preferredName") && (
                        <div id="field-preferredName" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Preferred Name {star("preferredName")}
                            </label>
                            <Input
                                placeholder="Enter your preferred name"
                                className={`h-12 rounded-xl ${fieldErrorClass("preferredName")}`}
                                value={values.preferredName}
                                onFocus={() => clearHint("preferredName")}
                                onChange={(event) => handleChange("preferredName", event.target.value)}
                            />
                            {hint("preferredName")}
                        </div>
                        )}
                        {show("lastName") && (
                        <div id="field-lastName" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Last Name {star("lastName")}
                            </label>
                            <Input
                                placeholder="Enter your last name"
                                className={`h-12 rounded-xl ${fieldErrorClass("lastName")}`}
                                value={values.lastName}
                                onFocus={() => clearHint("lastName")}
                                onChange={(ev) => handleChange("lastName", ev.target.value)}
                            />
                            {hint("lastName")}
                        </div>
                        )}
                        {show("citizenship") && (
                        <div id="field-citizenship" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Citizenship {star("citizenship")}
                            </label>
                            {fieldType("citizenship") === "country" && countries.length > 0 ? (
                                <SearchablePicklist
                                    options={countries}
                                    value={values.citizenship}
                                    onChange={(val) => handleChange("citizenship", val)}
                                    placeholder="Select your citizenship"
                                    searchPlaceholder="Search countries..."
                                    invalid={!!errors["citizenship"]}
                                />
                            ) : (
                                <Input
                                    placeholder="Select your citizenship"
                                    className={`h-12 rounded-xl ${fieldErrorClass("citizenship")}`}
                                    value={values.citizenship}
                                    onFocus={() => clearHint("citizenship")}
                                    onChange={(ev) => handleChange("citizenship", ev.target.value)}
                                />
                            )}
                            {hint("citizenship")}
                        </div>
                        )}
                        {show("secondaryCitizenship") && (
                        <div id="field-secondaryCitizenship" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Secondary Citizenship {star("secondaryCitizenship")}
                            </label>
                            {fieldType("secondaryCitizenship") === "country" && countries.length > 0 ? (
                                <SearchablePicklist
                                    options={countries}
                                    value={values.secondaryCitizenship}
                                    onChange={(val) => handleChange("secondaryCitizenship", val)}
                                    placeholder="Select secondary citizenship"
                                    searchPlaceholder="Search countries..."
                                    invalid={!!errors["secondaryCitizenship"]}
                                />
                            ) : (
                                <Input
                                    placeholder="Select secondary citizenship"
                                    className={`h-12 rounded-xl ${fieldErrorClass("secondaryCitizenship")}`}
                                    value={values.secondaryCitizenship}
                                    onFocus={() => clearHint("secondaryCitizenship")}
                                    onChange={(ev) => handleChange("secondaryCitizenship", ev.target.value)}
                                />
                            )}
                            {hint("secondaryCitizenship")}
                        </div>
                        )}
                        {show("gender") && (
                        <div id="field-gender" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Gender {star("gender")}
                            </label>
                            <select
                                className={`flex h-12 w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50 ${errors.gender ? "border-red-400 ring-1 ring-red-200" : "border-slate-800"}`}
                                value={values.gender}
                                onFocus={() => clearHint("gender")}
                                onChange={(ev) => handleChange("gender", ev.target.value)}
                            >
                                <option value="">Select gender</option>
                                {genderIdentities.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            {hint("gender")}
                        </div>
                        )}
                        {show("nationalIdentityNumber") && (
                        <div id="field-nationalIdentityNumber" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                National Identity Number {star("nationalIdentityNumber")}
                            </label>
                            <Input
                                placeholder="Enter national identity number"
                                className={`h-12 rounded-xl ${fieldErrorClass("nationalIdentityNumber")}`}
                                value={values.nationalIdentityNumber}
                                maxLength={20}
                                onFocus={() => clearHint("nationalIdentityNumber")}
                                onChange={(ev) => handleChange("nationalIdentityNumber", ev.target.value)}
                            />
                            {hint("nationalIdentityNumber")}
                        </div>
                        )}
                        {show("dateOfBirth") && (
                        <div id="field-dateOfBirth" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Date of Birth {star("dateOfBirth")}
                            </label>
                            <Input
                                type="date"
                                className={`h-12 rounded-xl ${fieldErrorClass("dateOfBirth")}`}
                                value={values.dateOfBirth}
                                min={getMinBirthDate()}
                                max={getMaxBirthDate()}
                                onChange={(ev) => handleChange("dateOfBirth", ev.target.value)}
                            />
                            {hint("dateOfBirth")}
                        </div>
                        )}
                        {show("countryOfBirth") && (
                        <div id="field-countryOfBirth" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Country of Birth {star("countryOfBirth")}
                            </label>
                            {fieldType("countryOfBirth") === "country" && countries.length > 0 ? (
                                <SearchablePicklist
                                    options={countries}
                                    value={values.countryOfBirth}
                                    onChange={(val) => handleChange("countryOfBirth", val)}
                                    placeholder="Select country of birth"
                                    searchPlaceholder="Search countries..."
                                    invalid={!!errors["countryOfBirth"]}
                                />
                            ) : (
                                <Input
                                    placeholder="Enter country of birth"
                                    className={`h-12 rounded-xl ${fieldErrorClass("countryOfBirth")}`}
                                    value={values.countryOfBirth}
                                    onFocus={() => clearHint("countryOfBirth")}
                                    onChange={(ev) => handleChange("countryOfBirth", ev.target.value)}
                                />
                            )}
                            {hint("countryOfBirth")}
                        </div>
                        )}
                        {show("cityOfBirth") && (
                        <div id="field-cityOfBirth" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                City of Birth {star("cityOfBirth")}
                            </label>
                            <Input
                                placeholder="Enter city of birth"
                                className={`h-12 rounded-xl ${fieldErrorClass("cityOfBirth")}`}
                                value={values.cityOfBirth}
                                onFocus={() => clearHint("cityOfBirth")}
                                onChange={(ev) => handleChange("cityOfBirth", ev.target.value)}
                            />
                            {hint("cityOfBirth")}
                        </div>
                        )}
                    </div>
                </section>
                )}

                {/* Identity Document Section */}
                {showSection("identityNumber", "identityDateOfIssue", "identityDateOfExpiry", "identityIssuingCountry") && (
                <section className="space-y-6">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#001B44]" />
                        Identity Document
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        {show("identityNumber") && (
                        <div id="field-identityNumber" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Passport Number {star("identityNumber")}
                            </label>
                            <Input
                                placeholder="Enter passport number"
                                className={`h-12 rounded-xl ${fieldErrorClass("identityNumber")}`}
                                value={values.identityNumber}
                                maxLength={20}
                                onFocus={() => clearHint("identityNumber")}
                                onChange={(ev) => handleChange("identityNumber", ev.target.value)}
                            />
                            {hint("identityNumber")}
                        </div>
                        )}
                        {show("identityIssuingCountry") && (
                        <div id="field-identityIssuingCountry" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Passport Issuing Country {star("identityIssuingCountry")}
                            </label>
                            {fieldType("identityIssuingCountry") === "country" && countries.length > 0 ? (
                                <SearchablePicklist
                                    options={countries}
                                    value={values.identityIssuingCountry}
                                    onChange={(val) => handleChange("identityIssuingCountry", val)}
                                    placeholder="Select issuing country"
                                    searchPlaceholder="Search countries..."
                                    invalid={!!errors["identityIssuingCountry"]}
                                />
                            ) : (
                                <Input
                                    placeholder="Select issuing country"
                                    className={`h-12 rounded-xl ${fieldErrorClass("identityIssuingCountry")}`}
                                    value={values.identityIssuingCountry}
                                    onFocus={() => clearHint("identityIssuingCountry")}
                                    onChange={(ev) => handleChange("identityIssuingCountry", ev.target.value)}
                                />
                            )}
                            {hint("identityIssuingCountry")}
                        </div>
                        )}
                        {show("identityDateOfIssue") && (
                        <div id="field-identityDateOfIssue" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Passport Date of Issue {star("identityDateOfIssue")}
                            </label>
                            <Input
                                type="date"
                                className={`h-12 rounded-xl ${fieldErrorClass("identityDateOfIssue")}`}
                                value={values.identityDateOfIssue}
                                min={getMinPassportIssueDate()}
                                max={formatDateForInput(new Date())}
                                onChange={(ev) => handleChange("identityDateOfIssue", ev.target.value)}
                            />
                            {hint("identityDateOfIssue")}
                        </div>
                        )}
                        {show("identityDateOfExpiry") && (
                        <div id="field-identityDateOfExpiry" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Passport Date of Expiry {star("identityDateOfExpiry")}
                            </label>
                            <Input
                                type="date"
                                className={`h-12 rounded-xl ${fieldErrorClass("identityDateOfExpiry")}`}
                                value={values.identityDateOfExpiry}
                                min={(() => { const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); return formatDateForInput(tomorrow); })()}
                                max={getMaxPassportExpiryDate()}
                                onChange={(ev) => handleChange("identityDateOfExpiry", ev.target.value)}
                            />
                            {hint("identityDateOfExpiry")}
                        </div>
                        )}
                    </div>
                </section>
                )}

                {/* Contact Information Section */}
                {showSection("email", "mobileNumber") && (
                <section className="space-y-6">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#001B44]" />
                        Contact Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        {show("email") && (
                        <div id="field-email" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Email {star("email")}
                            </label>
                            <Input
                                type="email"
                                placeholder="Enter your email"
                                className={`h-12 rounded-xl ${fieldErrorClass("email")}`}
                                value={values.email}
                                onChange={(ev) => handleChange("email", ev.target.value)}
                            />
                            {hint("email")}
                        </div>
                        )}
                        {show("mobileNumber") && (
                        <div id="field-mobileNumber" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Mobile {star("mobileNumber")}
                            </label>
                            <div className="flex gap-2">
                                {show("mobileNumber") && (
                                    <div className="w-36 shrink-0">
                                        <PhoneCodePicker
                                            value={values.mobileCode}
                                            onChange={(code) => onChange("mobileCode", code)}
                                        />
                                    </div>
                                )}
                                <Input
                                    type="tel"
                                    placeholder="5550000000"
                                    className={`h-12 rounded-xl flex-1 ${fieldErrorClass("mobileNumber")}`}
                                    maxLength={15}
                                    value={values.mobileNumber}
                                    onFocus={() => clearHint("mobileNumber")}
                                    onChange={(ev) => handleChange("mobileNumber", ev.target.value)}
                                />
                            </div>
                            {hint("mobileNumber")}
                        </div>
                        )}
                    </div>
                </section>
                )}

                {/* Family Information Section */}
                {(show("fatherName") || show("motherName")) && (
                <section className="space-y-6">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#001B44]" />
                        Family Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        {show("fatherName") && (
                        <div id="field-fatherName" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Father Name {star("fatherName")}
                            </label>
                            <Input
                                placeholder="Enter father's name"
                                className={`h-12 rounded-xl ${fieldErrorClass("fatherName")}`}
                                maxLength={40}
                                value={values.fatherName}
                                onFocus={() => clearHint("fatherName")}
                                onChange={(event) => handleChange("fatherName", event.target.value)}
                            />
                            {hint("fatherName")}
                        </div>
                        )}
                        {show("motherName") && (
                        <div id="field-motherName" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Mother Name {star("motherName")}
                            </label>
                            <Input
                                placeholder="Enter mother's name"
                                className={`h-12 rounded-xl ${fieldErrorClass("motherName")}`}
                                maxLength={40}
                                value={values.motherName}
                                onFocus={() => clearHint("motherName")}
                                onChange={(event) => handleChange("motherName", event.target.value)}
                            />
                            {hint("motherName")}
                        </div>
                        )}
                    </div>
                </section>
                )}

                {/* Address Information Section */}
                {(show("addressCountry") || show("addressStreet") || show("addressCity") || show("addressZip")) && (
                <section className="space-y-6">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#001B44]" />
                        Address Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        {show("addressCountry") && (
                        <div id="field-addressCountry" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Address: Country {star("addressCountry")}
                            </label>
                            {fieldType("addressCountry") === "country" && countries.length > 0 ? (
                                <SearchablePicklist
                                    options={countries}
                                    value={values.addressCountry}
                                    onChange={(val) => handleChange("addressCountry", val)}
                                    placeholder="Select country"
                                    searchPlaceholder="Search countries..."
                                    invalid={!!errors["addressCountry"]}
                                />
                            ) : (
                                <Input
                                    placeholder="Select country"
                                    className={`h-12 rounded-xl ${fieldErrorClass("addressCountry")}`}
                                    value={values.addressCountry}
                                    onFocus={() => clearHint("addressCountry")}
                                    onChange={(ev) => handleChange("addressCountry", ev.target.value)}
                                />
                            )}
                            {hint("addressCountry")}
                        </div>
                        )}
                        {show("addressStreet") && (
                        <div id="field-addressStreet" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Address: Street {star("addressStreet")}
                            </label>
                            <Input
                                placeholder="Enter street address"
                                className={`h-12 rounded-xl ${fieldErrorClass("addressStreet")}`}
                                value={values.addressStreet}
                                onFocus={() => clearHint("addressStreet")}
                                onChange={(ev) => handleChange("addressStreet", ev.target.value)}
                            />
                            {hint("addressStreet")}
                        </div>
                        )}
                        {show("addressCity") && (
                        <div id="field-addressCity" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Address: City {star("addressCity")}
                            </label>
                            <Input
                                placeholder="Enter city"
                                className={`h-12 rounded-xl ${fieldErrorClass("addressCity")}`}
                                value={values.addressCity}
                                onFocus={() => clearHint("addressCity")}
                                onChange={(ev) => handleChange("addressCity", ev.target.value)}
                            />
                            {hint("addressCity")}
                        </div>
                        )}
                        {show("addressZip") && (
                        <div id="field-addressZip" className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1">
                                Address: Zip Code {star("addressZip")}
                            </label>
                            <Input
                                placeholder="Enter zip code"
                                className={`h-12 rounded-xl ${fieldErrorClass("addressZip")}`}
                                value={values.addressZip}
                                onFocus={() => clearHint("addressZip")}
                                onChange={(ev) => handleChange("addressZip", ev.target.value)}
                            />
                            {hint("addressZip")}
                        </div>
                        )}
                    </div>
                </section>
                )}

                {/* Additional Information Section */}
                {show("isAlumni") && (
                <section className="space-y-6">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#001B44]" />
                        Additional Information
                    </h4>
                    <div className="flex items-center space-x-3 p-4 rounded-xl border border-slate-100 bg-slate-50/30">
                        <input
                            type="checkbox"
                            id="alumni"
                            className="w-4 h-4 rounded border-slate-300 text-[#001B44] focus:ring-[#001B44]"
                            checked={values.isAlumni}
                            onChange={(event) => onChange("isAlumni", event.target.checked)}
                        />
                        <label htmlFor="alumni" className="text-xs font-bold text-slate-900">
                            I am an alumni student for Eduhub University
                        </label>
                    </div>
                </section>
                )}
            </div>
            {error && (
                <p className="text-sm text-red-500 font-semibold text-center mt-4">{error}</p>
            )}
        </form>
        </div>
    );
}
